import {
  CargoProblemAssigneeInputSchema,
  CargoProblemAssigneeCandidatesResponseSchema,
  CargoProblemAttachmentUploadInputSchema,
  CargoProblemActivityResponseSchema,
  CargoProblemAttentionResponseSchema,
  CargoProblemCargoCandidatesResponseSchema,
  CargoProblemDetailResponseSchema,
  CargoProblemFileDownloadResponseSchema,
  CargoProblemFilterOptionsSchema,
  CargoProblemListResponseSchema,
  CargoProblemMaterialDeleteInputSchema,
  CargoProblemMaterialsResponseSchema,
  CargoProblemMessageCreateInputSchema,
  CargoProblemMessageResponseSchema,
  CargoProblemReadInputSchema,
  CargoProblemReadResponseSchema,
  CargoProblemSummarySchema,
  CargoProblemWorkspaceCapabilitiesResponseSchema,
  CargoProblemWorkspaceMessagesResponseSchema,
  CargoProblemWorkspaceTransitionInputSchema,
  CreateCargoProblemWorkspaceInputSchema,
  StorageUploadUrlResponseSchema,
  type CargoProblemActor,
  type CargoProblemAssigneeCandidatesResponse,
  type CargoProblemAttachmentUploadInput,
  type CargoProblemListQuery,
  type CargoProblemMaterialsQuery,
  type CargoProblemMessageCreateInput,
  type CargoProblemQueue,
  type CargoProblemTransitionInput,
  type CargoProblemWorkspaceCapabilitiesResponse,
  type CargoProblemWorkspaceMessagesQuery,
  type CargoProblemWorkspaceTransitionInput,
  type CreateCargoProblemInput,
  type CreateCargoProblemWorkspaceInput,
  type PatchCargoProblemInput,
  type StorageUploadUrlResponse,
} from '@magimp/shared';
import { z } from 'zod';
import { request } from './client.js';

export type CargoProblemListResponse = z.infer<typeof CargoProblemListResponseSchema>;
export type CargoProblemDetailResponse = z.infer<typeof CargoProblemDetailResponseSchema>;
export type CargoProblemSummary = z.infer<typeof CargoProblemSummarySchema>;
export type CargoProblemFilterOptions = z.infer<typeof CargoProblemFilterOptionsSchema>;
export type CargoProblemCargoCandidates = z.infer<typeof CargoProblemCargoCandidatesResponseSchema>;
export type CargoProblemActivityResponse = z.infer<typeof CargoProblemActivityResponseSchema>;
export type CargoProblemAttentionResponse = z.infer<typeof CargoProblemAttentionResponseSchema>;
export type CargoProblemMaterialsResponse = z.infer<typeof CargoProblemMaterialsResponseSchema>;
export type CargoProblemWorkspaceMessagesResponse = z.infer<
  typeof CargoProblemWorkspaceMessagesResponseSchema
>;

export const CARGO_PROBLEMS_QUERY_ROOT = ['cargo-problems'] as const;

function query(input: CargoProblemListQuery): Record<string, string | boolean | undefined> {
  return { ...input };
}

export function listCargoProblems(
  input: CargoProblemListQuery,
  signal?: AbortSignal,
): Promise<CargoProblemListResponse> {
  return request(
    '/cargo-problems',
    { method: 'GET' },
    { query: query(input), ...(signal ? { signal } : {}) },
    CargoProblemListResponseSchema,
  );
}

export function getCargoProblemSummary(
  input: CargoProblemListQuery,
  signal?: AbortSignal,
): Promise<CargoProblemSummary> {
  return request(
    '/cargo-problems/summary',
    { method: 'GET' },
    { query: query(input), ...(signal ? { signal } : {}) },
    CargoProblemSummarySchema,
  );
}

export function getCargoProblemFilterOptions(
  input: CargoProblemListQuery,
  signal?: AbortSignal,
): Promise<CargoProblemFilterOptions> {
  return request(
    '/cargo-problems/filter-options',
    { method: 'GET' },
    { query: query(input), ...(signal ? { signal } : {}) },
    CargoProblemFilterOptionsSchema,
  );
}

export function getCargoProblemAttention(
  signal?: AbortSignal,
): Promise<CargoProblemAttentionResponse> {
  return request(
    '/cargo-problems/workspace/attention',
    { method: 'GET' },
    { ...(signal ? { signal } : {}) },
    CargoProblemAttentionResponseSchema,
  );
}

export function getCargoProblem(
  id: string,
  signal?: AbortSignal,
): Promise<CargoProblemDetailResponse> {
  return request(
    `/cargo-problems/${encodeURIComponent(id)}`,
    { method: 'GET' },
    { ...(signal ? { signal } : {}) },
    CargoProblemDetailResponseSchema,
  );
}

export function createCargoProblem(
  input: CreateCargoProblemInput,
): Promise<CargoProblemDetailResponse> {
  return request(
    '/cargo-problems',
    {
      method: 'POST',
      body: JSON.stringify(input),
      headers: { 'content-type': 'application/json' },
    },
    {},
    CargoProblemDetailResponseSchema,
  );
}

export function createCargoProblemWorkspace(
  input: CreateCargoProblemWorkspaceInput,
): Promise<CargoProblemDetailResponse> {
  return request(
    '/cargo-problems/workspace',
    {
      method: 'POST',
      body: JSON.stringify(CreateCargoProblemWorkspaceInputSchema.parse(input)),
      headers: { 'content-type': 'application/json' },
    },
    {},
    CargoProblemDetailResponseSchema,
  );
}

export function replaceCargoProblemWorkspace(
  id: string,
  input: CreateCargoProblemWorkspaceInput,
): Promise<CargoProblemDetailResponse> {
  return request(
    `/cargo-problems/${encodeURIComponent(id)}/workspace`,
    {
      method: 'PATCH',
      body: JSON.stringify(CreateCargoProblemWorkspaceInputSchema.parse(input)),
      headers: { 'content-type': 'application/json' },
    },
    {},
    CargoProblemDetailResponseSchema,
  );
}

export function patchCargoProblem(
  id: string,
  input: PatchCargoProblemInput,
): Promise<CargoProblemDetailResponse> {
  return request(
    `/cargo-problems/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(input),
      headers: { 'content-type': 'application/json' },
    },
    {},
    CargoProblemDetailResponseSchema,
  );
}

export function deleteCargoProblem(id: string): Promise<void> {
  return request(`/cargo-problems/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function transitionCargoProblem(
  id: string,
  input: CargoProblemTransitionInput,
): Promise<CargoProblemDetailResponse> {
  return request(
    `/cargo-problems/${encodeURIComponent(id)}/transitions`,
    {
      method: 'POST',
      body: JSON.stringify(input),
      headers: { 'content-type': 'application/json' },
    },
    {},
    CargoProblemDetailResponseSchema,
  );
}

export function transitionCargoProblemWorkspace(
  id: string,
  input: CargoProblemWorkspaceTransitionInput,
): Promise<CargoProblemDetailResponse> {
  return request(
    `/cargo-problems/${encodeURIComponent(id)}/workspace/transitions`,
    {
      method: 'POST',
      body: JSON.stringify(CargoProblemWorkspaceTransitionInputSchema.parse(input)),
      headers: { 'content-type': 'application/json' },
    },
    {},
    CargoProblemDetailResponseSchema,
  );
}

export function getCargoProblemAssigneeCandidates(
  queue: CargoProblemQueue,
  signal?: AbortSignal,
): Promise<CargoProblemAssigneeCandidatesResponse> {
  return request(
    '/cargo-problems/assignee-candidates',
    { method: 'GET' },
    { query: { queue }, ...(signal ? { signal } : {}) },
    CargoProblemAssigneeCandidatesResponseSchema,
  );
}

export function getProblemAssigneeCandidates(
  id: string,
  signal?: AbortSignal,
): Promise<CargoProblemAssigneeCandidatesResponse> {
  return request(
    `/cargo-problems/${encodeURIComponent(id)}/assignee-candidates`,
    { method: 'GET' },
    { ...(signal ? { signal } : {}) },
    CargoProblemAssigneeCandidatesResponseSchema,
  );
}

export function getCargoProblemWorkspaceCapabilities(
  id: string,
  signal?: AbortSignal,
): Promise<CargoProblemWorkspaceCapabilitiesResponse> {
  return request(
    `/cargo-problems/${encodeURIComponent(id)}/workspace/capabilities`,
    { method: 'GET' },
    { ...(signal ? { signal } : {}) },
    CargoProblemWorkspaceCapabilitiesResponseSchema,
  );
}

export function reassignCargoProblem(
  id: string,
  assigneeId: CargoProblemActor['id'],
): Promise<CargoProblemDetailResponse> {
  return request(
    `/cargo-problems/${encodeURIComponent(id)}/assignee`,
    {
      method: 'PATCH',
      body: JSON.stringify(CargoProblemAssigneeInputSchema.parse({ assigneeId })),
      headers: { 'content-type': 'application/json' },
    },
    {},
    CargoProblemDetailResponseSchema,
  );
}

export function issueCargoProblemAttachmentUpload(
  id: string,
  input: CargoProblemAttachmentUploadInput,
): Promise<StorageUploadUrlResponse> {
  return request(
    `/cargo-problems/${encodeURIComponent(id)}/workspace/attachments/upload-url`,
    {
      method: 'POST',
      body: JSON.stringify(CargoProblemAttachmentUploadInputSchema.parse(input)),
      headers: { 'content-type': 'application/json' },
    },
    {},
    StorageUploadUrlResponseSchema,
  );
}

export function getCargoProblemWorkspaceMessages(
  id: string,
  input: Partial<CargoProblemWorkspaceMessagesQuery>,
  signal?: AbortSignal,
): Promise<CargoProblemWorkspaceMessagesResponse> {
  return request(
    `/cargo-problems/${encodeURIComponent(id)}/workspace/messages`,
    { method: 'GET' },
    { query: { ...input }, ...(signal ? { signal } : {}) },
    CargoProblemWorkspaceMessagesResponseSchema,
  );
}

export function postCargoProblemWorkspaceMessage(
  id: string,
  input: CargoProblemMessageCreateInput,
): Promise<z.infer<typeof CargoProblemMessageResponseSchema>> {
  return request(
    `/cargo-problems/${encodeURIComponent(id)}/workspace/messages`,
    {
      method: 'POST',
      body: JSON.stringify(CargoProblemMessageCreateInputSchema.parse(input)),
      headers: { 'content-type': 'application/json' },
    },
    {},
    CargoProblemMessageResponseSchema,
  );
}

export function getCargoProblemMaterials(
  id: string,
  input: CargoProblemMaterialsQuery,
  signal?: AbortSignal,
): Promise<CargoProblemMaterialsResponse> {
  return request(
    `/cargo-problems/${encodeURIComponent(id)}/workspace/materials`,
    { method: 'GET' },
    { query: { ...input }, ...(signal ? { signal } : {}) },
    CargoProblemMaterialsResponseSchema,
  );
}

export function deleteCargoProblemMaterial(
  id: string,
  fileId: string,
  reason: string,
): Promise<void> {
  return request(
    `/cargo-problems/${encodeURIComponent(id)}/workspace/materials/${encodeURIComponent(fileId)}`,
    {
      method: 'DELETE',
      body: JSON.stringify(CargoProblemMaterialDeleteInputSchema.parse({ reason })),
      headers: { 'content-type': 'application/json' },
    },
  );
}

export function markCargoProblemRead(
  id: string,
  messageId: string,
): Promise<z.infer<typeof CargoProblemReadResponseSchema>> {
  return request(
    `/cargo-problems/${encodeURIComponent(id)}/workspace/read`,
    {
      method: 'POST',
      body: JSON.stringify(CargoProblemReadInputSchema.parse({ messageId })),
      headers: { 'content-type': 'application/json' },
    },
    {},
    CargoProblemReadResponseSchema,
  );
}

export function getCargoProblemActivity(
  id: string,
  signal?: AbortSignal,
): Promise<CargoProblemActivityResponse> {
  return request(
    `/cargo-problems/${encodeURIComponent(id)}/activity`,
    { method: 'GET' },
    { ...(signal ? { signal } : {}) },
    CargoProblemActivityResponseSchema,
  );
}

export function getCargoProblemFileDownloadUrl(id: string, fileId: string) {
  return request(
    `/cargo-problems/${encodeURIComponent(id)}/files/${encodeURIComponent(fileId)}/download-url`,
    { method: 'POST' },
    {},
    CargoProblemFileDownloadResponseSchema,
  );
}

export function searchCargoProblemCandidates(
  q: string,
  signal?: AbortSignal,
): Promise<CargoProblemCargoCandidates> {
  return request(
    '/cargo-problems/cargo-candidates',
    { method: 'GET' },
    { query: { q }, ...(signal ? { signal } : {}) },
    CargoProblemCargoCandidatesResponseSchema,
  );
}
